import React from "react";
import clsx from "clsx";
import { FiHeart } from "react-icons/fi";

const LIKED_HEART_COLOR = "#ff8a3d";

export interface PropertyLikeButtonProps {
    isLiked: boolean;
    likeCount: number;
    showCount?: boolean;
    isDisabled?: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onCountClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    variant?: "feed" | "slider" | "scoreboard";
    ariaLabel?: string;
}

/**
 * Heart control for property likes on puzzle feed cards and listing image slider.
 */
const PropertyLikeButton: React.FC<PropertyLikeButtonProps> = ({
    isLiked,
    likeCount,
    showCount = true,
    isDisabled = false,
    onClick,
    onCountClick,
    className,
    variant = "feed",
    ariaLabel,
}) => {
    const resolvedAriaLabel =
        ariaLabel ?? (isLiked ? "Unlike property" : "Like property");
    const displayCount = showCount && likeCount > 0;
    const isSlider = variant === "slider";
    const isScoreboard = variant === "scoreboard";
    const countClassName = clsx(
        "tw-text-[14px] tw-leading-none tw-font-medium",
        isSlider
            ? "tw-text-white tw-pr-0.5"
            : isScoreboard
              ? "tw-text-gray-400"
              : "tw-text-[#4B5563]",
    );
    return (
        <div className={clsx("tw-inline-flex tw-items-center tw-gap-1", className)}>
            <button
                type="button"
                aria-label={resolvedAriaLabel}
                aria-pressed={isLiked}
                disabled={isDisabled}
                className={clsx(
                    "tw-inline-flex tw-items-center tw-justify-center tw-border-0 tw-p-0",
                    isSlider
                        ? "tw-h-14 tw-w-14 tw-shrink-0 tw-rounded-full tw-bg-black tw-text-white tw-p-2 tw-shadow-[0_2px_10px_rgba(0,0,0,0.45)] tw-ring-1 tw-ring-black/25 disabled:tw-opacity-50"
                        : clsx(
                              "tw-bg-transparent",
                              isScoreboard
                                  ? "tw-text-gray-400 disabled:tw-opacity-50"
                                  : "tw-text-gray-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed",
                          ),
                )}
                onClick={onClick}
            >
                <FiHeart
                    className={clsx(
                        isSlider ? "tw-text-[34px]" : isScoreboard ? "tw-text-[18px]" : "tw-text-[24px]",
                        isLiked ? "tw-fill-current" : "tw-fill-none",
                        !isLiked && isScoreboard && "tw-text-gray-400",
                        isSlider && !isLiked && "tw-fill-white/30",
                    )}
                    style={
                        isLiked
                            ? { color: LIKED_HEART_COLOR }
                            : isSlider
                              ? {
                                    filter: "drop-shadow(0 0 1px rgba(0,0,0,0.9)) drop-shadow(0 1px 3px rgba(0,0,0,0.55))",
                                }
                              : undefined
                    }
                    stroke="currentColor"
                    strokeWidth={isLiked ? 0 : 2}
                />
            </button>
            {displayCount && (
                onCountClick ? (
                    <button
                        type="button"
                        aria-label={`View ${likeCount} likes`}
                        disabled={isDisabled}
                        className={clsx(
                            "tw-inline-flex tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer hover:tw-opacity-80",
                            isScoreboard ? "tw-text-gray-400 disabled:tw-opacity-50" : "disabled:tw-opacity-50",
                        )}
                        onClick={(event) => {
                            event.stopPropagation();
                            onCountClick(event);
                        }}
                    >
                        <span className={countClassName}>{likeCount}</span>
                    </button>
                ) : (
                    <span className={countClassName}>{likeCount}</span>
                )
            )}
        </div>
    );
};

export default PropertyLikeButton;
